import type { ChatMessage, ChatThread } from "./types";

export const THREADS_KEY_PREFIX = "chatforge.threads.v2";
const THREADS_KEY_V1 = "chatforge.threads.v1";
const V1_MIGRATED = "chatforge.threads.v1.migrated";

export function memoryModelKey(raw?: string | null): string {
  const s = (raw || "").trim().toLowerCase();
  return s || "none";
}

/** Filesystem-safe model id for the HF bucket path. */
export function memoryModelSlug(raw?: string | null): string {
  const s = memoryModelKey(raw)
    .replace(/[^a-z0-9._-]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return (s || "none").slice(0, 80);
}

export function threadsKey(email: string, modelKey = "none"): string {
  const id = (email || "guest").trim().toLowerCase() || "guest";
  return `${THREADS_KEY_PREFIX}:${id}:${memoryModelKey(modelKey)}`;
}

function threadsKeyV1(email: string): string {
  const id = (email || "guest").trim().toLowerCase() || "guest";
  return `${THREADS_KEY_V1}:${id}`;
}

export function newId(): string {
  return crypto.randomUUID();
}

export function titleFromFirstLine(text: string): string {
  const line = text.replace(/\s+/g, " ").trim();
  if (!line) return "New chat";
  return line.length > 42 ? `${line.slice(0, 41)}…` : line;
}

export function emptyThread(modelKey = "none"): ChatThread {
  const now = Date.now();
  return {
    id: newId(),
    title: "New chat",
    messages: [],
    updatedAt: now,
    modelKey: memoryModelKey(modelKey),
  };
}

function readThreadList(key: string): ChatThread[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isThread);
  } catch {
    return [];
  }
}

export function loadThreads(email: string, modelKey = "none"): ChatThread[] {
  const want = memoryModelKey(modelKey);
  const v2 = readThreadList(threadsKey(email, want));
  if (v2.length) return v2.map((t) => ({ ...t, modelKey: t.modelKey || want }));
  if (typeof localStorage === "undefined") return [];
  if (localStorage.getItem(V1_MIGRATED)) return [];
  const v1 = readThreadList(threadsKeyV1(email));
  if (!v1.length) return [];
  localStorage.setItem(V1_MIGRATED, want);
  return v1.map((t) => ({ ...t, modelKey: want }));
}

export function saveThreads(email: string, threads: ChatThread[], modelKey = "none"): void {
  if (typeof localStorage === "undefined") return;
  const want = memoryModelKey(modelKey);
  const stamped = threads.map((t) => ({ ...t, modelKey: t.modelKey || want }));
  localStorage.setItem(threadsKey(email, want), JSON.stringify(stamped));
}

function isThread(v: unknown): v is ChatThread {
  if (!v || typeof v !== "object") return false;
  const t = v as ChatThread;
  return typeof t.id === "string" && typeof t.title === "string" && Array.isArray(t.messages);
}

export function upsertThread(threads: ChatThread[], next: ChatThread): ChatThread[] {
  const rest = threads.filter((t) => t.id !== next.id);
  return [next, ...rest].sort((a, b) => b.updatedAt - a.updatedAt);
}

export function mergeThreads(local: ChatThread[], remote: ChatThread[]): ChatThread[] {
  const byId = new Map<string, ChatThread>();
  for (const t of [...remote, ...local]) {
    const prev = byId.get(t.id);
    if (!prev || (t.updatedAt || 0) >= (prev.updatedAt || 0)) byId.set(t.id, t);
  }
  return [...byId.values()].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

export function appendMessage(thread: ChatThread, msg: ChatMessage): ChatThread {
  const messages = [...thread.messages, msg];
  const title =
    thread.messages.length === 0 && msg.role === "user"
      ? titleFromFirstLine(msg.content)
      : thread.title;
  return { ...thread, title, messages, updatedAt: Date.now() };
}

export function patchMessage(
  thread: ChatThread,
  id: string,
  content: string,
): ChatThread {
  return {
    ...thread,
    updatedAt: Date.now(),
    messages: thread.messages.map((m) => (m.id === id ? { ...m, content } : m)),
  };
}
